import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./DisclaimerScreen.css";
import { logAuthEvent } from "../../utils/logAuthEvent";
import { trackProductEvent } from "@/utils/analytics/track";
import { updateAgreedToTerms } from "@/api/users.api";

const DisclaimerScreen = (props) => {
  const [disclaimer, setDisclaimer] = useState(false);
  const [privacy, setPrivacy] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const exitTrackedRef = useRef(false);

  useEffect(() => {
    exitTrackedRef.current = false;
    const trackExit = (reason) => {
      if (exitTrackedRef.current) return;
      trackProductEvent(
        "terms_gate_exit",
        {
          page: location.pathname,
          role: props.user?.role,
          reason,
        },
        props.user,
        { allowWithoutConsent: true },
      );
      exitTrackedRef.current = true;
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        trackExit("hidden");
      }
    };
    const handleBeforeUnload = () => trackExit("exit");
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [props.user?._id, location.pathname]);

  async function senddata() {
    const agreedToTerms = disclaimer;

    try {
      const response = await updateAgreedToTerms(agreedToTerms);

      if (response) {
        logAuthEvent({
          message: `Update successful for user: ${props.user._id}`,
          level: "info",
          capture: "true",
        });
        trackProductEvent(
          "funnel_step",
          {
            funnel: "onboarding",
            step: "terms_accepted",
            page: location.pathname,
            role: props.user?.role,
          },
          props.user,
          { allowWithoutConsent: true },
        );
        trackProductEvent(
          "terms_gate_accept",
          {
            page: location.pathname,
            role: props.user?.role,
          },
          props.user,
          { allowWithoutConsent: true },
        );
        exitTrackedRef.current = true;
        if (!location?.pathname || location.pathname === "/") {
          navigate("/");
        }
      }
    } catch (error) {
      logAuthEvent({
        message: "Failed to update user",
        error,
        level: "error",
        capture: true,
      });
    }
    // Force a full page reload
    window.location.reload();
  }

  return (
    <div className="disclaimer-container">
      <div className="disclaimer-content">
        <div className="disclaimer-header">
          <h1 className="welcome-title flex gap-2">
            <span>Welcome</span>
            <span>{props.user?.displayName ?? props.user.email}!</span>
          </h1>
          <p className="welcome-subtitle">
            Please review and accept the terms before getting started
          </p>
        </div>
        <div className="disclaimer-section">
          <h2 className="section-title">Disclaimer</h2>
          <div className="disclaimer-text">
            <p
              style={{
                fontWeight: "bold",
                marginBottom: "15px",
                color: "#4cc68d",
                fontSize: "13px",
                textAlign: "center",
                padding: "10px",
                backgroundColor: "rgba(76, 198, 141, 0.1)",
                borderRadius: "8px",
                border: "2px solid #4cc68d",
              }}
            >
              Welcome to 1-2-3 Wellness! We are so glad that YOU are here!
            </p>
            By clicking this box, I acknowledge that this app, and the 1-2-3
            Wellness™ program, are educational in nature and intended to serve
            as a complement to support provided by talented people including
            teachers, parents, counselors, and other professionals. The app and
            program are not designed to treat or diagnose any mental health
            issues. You are encouraged to stop using this app and affiliated
            resources at any time. Please let your teacher, parent, guardian,
            counselor, peer, or principal know if you have any concerns at all.
            The 1-2-3 Wellness™ program offers support that is educational in
            nature. The program is intended to serve as a part of a larger
            framework of support orchestrated by schools and organizations.
            1-2-3 Wellness™ is not intended to replace other vital supports or
            to treat or diagnose any illness. The fidelity of the 1-2-3
            Wellness™ program occurs when the program is used in its entirety,
            complete with ongoing professional development and the full spectrum
            of resources, and in conjunction with the support of your
            organization's educational and mental health professionals. All
            resources and information provided via GrowthWell LLC programs are
            entirely voluntary and individual participants are encouraged to
            use, or not use, different resources according to what supports
            their unique well-being needs as guided by your school and/or
            organizational personnel. Please contact GrowthWell LLC immediately
            with any problems or concerns at info@123wellness.org. You are
            invited to reach out to our GrowthWell LLC staff at any time and
            please continue to consult educational and mental health
            professionals as part of ongoing efforts to meet student and other
            stakeholder needs. Please note that you can change your settings in
            your settings tab anytime.
          </div>

          <div className="terms-links">
            I have read and agree to the{" "}
            <a
              className="terms-link"
              href="https://www.123wellness.org/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.123wellness.org/privacypolicy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            , and{" "}
            <a
              className="terms-link"
              href="https://www.123wellness.org/data-governance"
              target="_blank"
              rel="noopener noreferrer"
            >
              Data Governance
            </a>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={disclaimer}
                onChange={() => setDisclaimer(!disclaimer)}
                className="modern-checkbox"
                data-analytics-id="terms_checkbox"
                data-analytics-label="Agree to terms"
              />
              <span className="checkmark"></span>I agree to the terms and
              conditions
            </label>
          </div>
        </div>

        <div className="disclaimer-section">
          <h2 className="section-title">Data Privacy</h2>
          <p className="section-description">
            I give permission for my teacher(s) and building administrators to
            view my data.
          </p>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={privacy}
                onChange={() => setPrivacy(!privacy)}
                className="modern-checkbox"
                data-analytics-id="privacy_checkbox"
                data-analytics-label="Agree to data sharing"
              />
              <span className="checkmark"></span>I agree to data sharing
            </label>
          </div>
        </div>

        <div className="encouragement-message">
          Have an excellent experience. Great job building your healthy wellness
          habits!
        </div>

        <div className="button-container">
          {disclaimer ? (
            <button
              onClick={() => senddata()}
              className="continue-button enabled"
              data-analytics-id="continue_onboarding"
              data-analytics-label="Continue onboarding"
            >
              Continue
            </button>
          ) : (
            <button disabled className="continue-button disabled">
              Continue
            </button>
          )}
        </div>

        <div className="footer-info">
          <p>© GrowthWell LLC 2026</p>
          <p>U.S. Patent No. 12,053,300</p>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerScreen;
